import axios from 'axios';
import Cookies from 'js-cookie';

/**
 * Support Dashboard API client.
 *
 * NO baseURL: the support SPA and its backend share a hostname (e.g.,
 * https://support.local.prompt-security.cudasvc.com). Using relative URLs
 * ensures every call stays same-origin so the support_sessionid cookie is
 * always sent. Setting baseURL to the customer's API_URL would send calls
 * cross-origin to staging.prompt-security.cudasvc.com — session cookies
 * would be dropped and each call would hit the wrong backend.
 *
 * Callers pass full paths including /api/v1/support/ (the copied guardian
 * saga code has these paths inline).
 *
 * withCredentials: true is required so the support_sessionid cookie is
 * sent on every request (see apps/support/middleware.py SessionCookieRouter).
 */
export const api = axios.create({
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Attach CSRF token on state-changing requests — Django's CSRF middleware
// pairs this header with the csrftoken cookie.
api.interceptors.request.use((config) => {
  const csrfToken = Cookies.get('csrftoken');
  if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

// The support login endpoints legitimately return 401 to signal
// "not authenticated" and must not trigger the global redirect-to-login.
const NO_REDIRECT_ON_401 = [
  '/api/v1/support/login/',
  '/api/v1/support/whoami/',
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const skipRedirect = NO_REDIRECT_ON_401.some((p) => url.includes(p));
      const currentPath = window.location.pathname;
      if (!skipRedirect && currentPath !== '/login') {
        window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: () => api.get('/api/v1/support/login/'),
  logout: () => api.post('/api/v1/support/logout/'),
  whoami: () => api.get('/api/v1/support/whoami/'),
};

export default api;
